/**
 * Shared people-directory hook — the single source of truth for
 * id→name resolution and the employee picker across EVERY module
 * (leave / expense / complaint approval surfaces, the 1:1 picker, the
 * People page). QA CRIT-3 / HIGH-1.
 *
 * Backed by GET /api/v1/directory: auth-only for ANY tenant user (NOT
 * the HR-only GET /employees), NOT feature-gated, returns ONLY non-PII
 * roster fields. So a plain employee / line manager gets a real name
 * map too — that's why the old performance useDirectory (which was
 * gated behind the HR-only /employees and degraded to "User #id" for
 * everyone else) is superseded by this.
 *
 * Resolving a colleague's name is a CONVENIENCE, never an authorization
 * signal — the backend re-checks every read/write in-tenant. We never
 * invent a name client-side: an unknown id falls back to "User #<id>".
 *
 * NOTE on the backend's own additive `*_name` fields: leave/expense/
 * complaint DTOs now carry user_name / approver_name / etc. directly,
 * so prefer the row's own name. This hook is the fallback resolver (and
 * the source for pickers + the People page), so a row whose name field
 * is null still resolves via the directory when the id is known.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { directoryApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { DirectoryEntry } from "@/types/directory";
import { personDetail, personLabel } from "@/lib/person-label";

const DIRECTORY_STALE = 5 * 60 * 1000; // roster changes rarely

export const directoryKeys = {
  all: ["directory"] as const,
  list: (
    includeInactive: boolean,
    page?: number,
    perPage?: number,
    q?: string,
    department?: string,
    reportsOfMe?: boolean,
  ) =>
    [
      ...directoryKeys.all,
      includeInactive ? "with-inactive" : "active",
      page ?? 0,
      perPage ?? 0,
      // Server-side filters (bug B-A6) — part of the cache key so a new
      // search/department refetches instead of serving a stale filtered set.
      q?.trim() || "",
      department?.trim() || "",
      // A manager's "My Team" list (reports only) is a different roster.
      ...(reportsOfMe ? (["reports-of-me"] as const) : []),
    ] as const,
};

/**
 * The directory query. `includeInactive` also returns deactivated users
 * (needed for id→name on historical approval rows whose actor may since
 * be deactivated). `enabled` lets a caller defer the fetch.
 * Pass `page` + `perPage` to paginate; omit for the full roster
 * (backwards-compat — pickers always want the full list).
 */
export function useDirectory(opts?: {
  includeInactive?: boolean;
  enabled?: boolean;
  page?: number;
  perPage?: number;
  /** Server-side name/role/team substring filter (bug B-A6). */
  q?: string;
  /** Server-side exact-department filter (bug B-A6). */
  department?: string;
  /** Only the caller's direct + indirect reports (?reports_of=me). */
  reportsOfMe?: boolean;
}) {
  const { user } = useAuth();
  const includeInactive = opts?.includeInactive ?? false;
  const page = opts?.page;
  const perPage = opts?.perPage;
  const search = opts?.q?.trim() || undefined;
  const department = opts?.department?.trim() || undefined;
  const reportsOfMe = opts?.reportsOfMe === true;
  // Only meaningful for a tenant user; a platform user (no tenant) 403s
  // — react-query retry:1 then degrades. Gate on a tenant being present.
  const enabled =
    (opts?.enabled ?? true) && !!user && user.tenant_id != null;

  const q = useQuery({
    queryKey: directoryKeys.list(includeInactive, page, perPage, search, department, reportsOfMe),
    queryFn: ({ signal }) =>
      directoryApi.listDirectory(
        { includeInactive, page, perPage, q: search, department, reportsOfMe },
        signal,
      ),
    staleTime: DIRECTORY_STALE,
    enabled,
  });

  const entries: DirectoryEntry[] = useMemo(
    () => q.data?.directory ?? [],
    [q.data],
  );

  const byId = useMemo(() => {
    const m = new Map<number, DirectoryEntry>();
    for (const e of entries) m.set(e.id, e);
    return m;
  }, [entries]);

  const myId = user?.user_id;

  /**
   * Label a user_id for display.
   *  - prefer an explicit `serverName` (the row's own *_name field) so a
   *    since-deactivated user not in the active list still renders;
   *  - else the directory display_name;
   *  - "You" for self (only when no explicit serverName is forced);
   *  - else a neutral "User #<id>" (NEVER invented).
   * Pass selfLabel:false to keep the real name even for self (lists
   * where "You" would be confusing — e.g. a People directory row).
   */
  function label(
    id: number | null | undefined,
    serverName?: string | null,
    selfLabel = true,
  ): string {
    if (id == null) return serverName?.trim() || "—";
    if (selfLabel && myId != null && id === myId && !serverName) {
      return "You";
    }
    const name = serverName?.trim();
    if (name) return name;
    const e = byId.get(id);
    if (e) return e.display_name || `${e.first_name} ${e.last_name}`.trim();
    return `User #${id}`;
  }

  /**
   * Like label() (never "You"), plus the employee code and department so
   * two people with the same name are distinguishable: "Meera Joshi
   * (ACM004 · Engineering)". Used wherever payroll names someone.
   */
  function labelWithCode(
    id: number | null | undefined,
    serverName?: string | null,
  ): string {
    const name = label(id, serverName, false);
    if (id == null) return name;
    const e = byId.get(id);
    if (!e) return name;
    return personLabel({ name, employeeCode: e.employee_code, department: e.department });
  }

  /** "ACM004 · Engineering" for an id ("" when unknown). */
  function detail(id: number | null | undefined): string {
    if (id == null) return "";
    const e = byId.get(id);
    return e ? personDetail({ employeeCode: e.employee_code, department: e.department }) : "";
  }

  return {
    /** All directory rows (active, or +inactive when requested). */
    entries,
    /** id → DirectoryEntry. */
    byId,
    /** id→name resolver (prefers an explicit server-provided name). */
    label,
    /** Name + employee code + department (duplicate-name safe). */
    labelWithCode,
    /** Employee code + department for an id. */
    detail,
    /** The signed-in user's id (for "You" framing). */
    myId,
    /** Count of the FILTERED roster (reflects q/department when set). */
    total: q.data?.total ?? 0,
    /**
     * Tenant-wide DISTINCT department list for the People dropdown (bug
     * B-A6) — every department, not just the current page's. Only populated
     * by the backend when paginating; undefined otherwise.
     */
    departments: q.data?.departments,
    /** What colleagues see about each other (work email / phone) — wave B. */
    contactVisibility: q.data?.contact_visibility,
    /** True only once the directory actually loaded (not just enabled). */
    hasDirectory: !q.isError && !!q.data,
    isLoading: q.isLoading && enabled,
    isError: q.isError,
    error: q.error,
  };
}
